/* eslint-disable no-prototype-builtins */
import moment from "moment";
import { atom, selector } from "recoil";

import {
  GetOrderByFilterDocument,
  GetOrderByFilterQuery,
  GetProductByFilterDocument,
  OrderStatus
} from "graphql/generated";
import graphQLClient from "graphql/react-query";
import { ConditionInterface } from "src/types/condition.d";
import { OrderInterface } from "src/types/order.d";
import { SectionStateType, SectionType } from "src/types/orderByCondition";

import keys from "./constants";
import { patientState, routerChangeState } from "./generalState";
import { productItemState } from "./stripeState";

export const conditionState = atom({
  key: keys.CONDITION_STATE,
  default: null
});

export const pendingPrescriptionOrdersState = selector<OrderInterface[]>({
  key: keys.PENDING_PRESCRIPTION_ORDERS_STATE,
  get: async ({ get }) => {
    const patient = get(patientState);

    const orderData = await graphQLClient.request<GetOrderByFilterQuery>(GetOrderByFilterDocument, {
      input: {
        patientId: patient.sysId,
        statuses: [OrderStatus.PendingPrescription, OrderStatus.PendingPrescriptionFu]
      }
    });

    const pendingPrescriptionOrders = orderData.getOrderByFilter || [];

    return pendingPrescriptionOrders.length === 0
      ? []
      : pendingPrescriptionOrders
          .filter((item) => moment(item?.expiredAt || 0).isAfter(Date.now(), "milliseconds"))
          .map((item) => ({ sysId: item.sysId, ...item }));
  }
});

export const pendingBookingOrdersState = selector<OrderInterface[]>({
  key: keys.PENDING_BOOKING_ORDERS_STATE,
  get: async ({ get }) => {
    get(routerChangeState);

    const patient = get(patientState);
    const orderData = await graphQLClient.request<GetOrderByFilterQuery>(GetOrderByFilterDocument, {
      input: {
        patientId: patient?.sysId,
        statuses: [OrderStatus.PendingBooking, OrderStatus.PendingBookingFu]
      }
    });

    const pendingBookingOrders = orderData.getOrderByFilter || [];

    return pendingBookingOrders.length === 0
      ? []
      : pendingBookingOrders.map((item) => ({ sysId: item.sysId, ...item }));
  }
});

export const sectionState = atom<SectionStateType>({
  key: keys.SECTION_STATE,
  default: SectionType.medsSelection
});

export const sectionStateHistory = atom<SectionStateType[]>({
  key: keys.SECTION_STATE_HISTORY,
  default: []
});

export const conditionSettingState = selector<ConditionInterface>({
  key: keys.CONDITION_SETTING_STATE,
  get: async ({ get }) => {
    const condition = get(conditionState);

    const productData = await graphQLClient.request(GetProductByFilterDocument, {
      input: {
        id: condition
      }
    });

    return condition ? productData?.getProductByFilter[0] : null;
  }
});

export const currentProductGroupIndexState = atom<number>({
  key: keys.CURRENT_PRODUCT_GROUP_INDEX_STATE,
  default: 0
});

export const selectedProductsState = atom<Object>({
  key: keys.SELECTED_PRODUCT_STATE,
  default: {}
});

export const selectedPricesState = atom<Object>({
  key: keys.SELECTED_PRICES_STATE,
  default: {}
});

export const notesState = atom<string>({
  key: keys.NOTES_STATE,
  default: ""
});

export const currentVariationIndexState = atom<number>({
  key: keys.CURRENT_VARIATION_INDEX_STATE,
  default: 0
});

export const currentProductState = selector<any>({
  key: keys.CURRENT_PRODUCT_STATE,
  get: ({ get }) => {
    const product: any = get(selectedProductsState)[get(currentProductGroupIndexState)];

    if (product) {
      return get(productItemState(product));
    }

    return null;
  }
});

export const selectedVariationsState = atom<any>({
  key: keys.SELECTED_VARIATIONS_STATE,
  default: {}
});

export const overwriteProductIdState = atom<string>({
  key: keys.OVERWRITE_PRODUCT_ID_STATE,
  default: ""
});

export const savedDOBState = atom<any>({
  key: keys.SAVED_DOB_STATE,
  default: ""
});
